import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Tabs from '@cloudscape-design/components/tabs';
import Box from '@cloudscape-design/components/box';
import { SearchForm } from './SearchForm';

type SearchTab = 'payment' | 'artifact' | 'execution';

interface TabConfig {
  id: SearchTab;
  label: string;
  placeholder: string;
  bulkPlaceholder: string;
}

const tabConfigs: TabConfig[] = [
  {
    id: 'payment',
    label: 'Payment',
    placeholder: 'Enter End-to-End ID, e.g. DASAVFAR7Y9W2DC',
    bulkPlaceholder: 'Enter multiple Payment IDs, one per line',
  },
  {
    id: 'artifact',
    label: 'Artifact',
    placeholder: 'Enter artifact file name, e.g. pain.001.001.03_2025-06-01.xml',
    bulkPlaceholder: 'Enter multiple artifact file names, one per line',
  },
  {
    id: 'execution',
    label: 'Execution',
    placeholder: 'Enter execution name',
    bulkPlaceholder: 'Enter multiple execution names, one per line',
  },
];

export const SearchTabs = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SearchTab>('payment');
  const [bulkMode, setBulkMode] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = () => {
    const trimmed = searchValue.trim();
    if (!trimmed) return;

    if (bulkMode) {
      // Bulk: route to the entity's server-side search results page.
      const ids = trimmed.split('\n').map(line => line.trim()).filter(Boolean);
      if (ids.length === 0) return;
      const bulkRoute = { payment: '/payments', artifact: '/artifacts', execution: '/executions' }[activeTab];
      navigate(`${bulkRoute}?ids=${ids.map(encodeURIComponent).join(',')}`);
      return;
    }

    // Single-identifier search → detail page.
    const route = { payment: '/payment', artifact: '/artifact', execution: '/execution' }[activeTab];
    navigate(`${route}?id=${encodeURIComponent(trimmed)}`);
  };

  return (
    <Tabs
      activeTabId={activeTab}
      onChange={({ detail }) => {
        setActiveTab(detail.activeTabId as SearchTab);
        setSearchValue('');
        setBulkMode(false);
      }}
      tabs={tabConfigs.map(config => ({
        id: config.id,
        label: config.label,
        content: (
          <Box padding={{ top: 'm' }}>
            <SearchForm
              placeholder={config.placeholder}
              bulkPlaceholder={config.bulkPlaceholder}
              bulkMode={bulkMode}
              onBulkModeChange={setBulkMode}
              searchValue={searchValue}
              onSearchValueChange={setSearchValue}
              onSearch={handleSearch}
            />
          </Box>
        ),
      }))}
    />
  );
};
